import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { toast } from "react-toastify";
import { VreEditor, fitTemplateToVideo } from "@wooglobe/vre-editor";
import useVideoMetadata from "./useVideoMetadata";
import { fitTemplateToVideos } from "./multiVideoDocument";

// m:ss formatter for the TikTok cover frame readout
export const formatSeconds = (secs) => {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const COVER_READOUT_MS = 250; // the playhead fires every frame; the readout needs ~4 updates/s

/**
 * The editor itself, memoised: the dialog and the cover readout re-render
 * around it, but with referentially stable props it never re-renders with them.
 */
const EditorPane = React.memo(function EditorPane({ editorKey, ...props }) {
  return (
    <Box sx={{ position: "absolute", inset: 0 }}>
      <VreEditor key={editorKey} liveBackend {...props} />
    </Box>
  );
});

/**
 * Step 1 of the video editor: the template fitted to this video, in the editor.
 *
 * `docRef` ({ key, doc }) belongs to the dialog: it always holds the latest
 * document, and lets the editor reopen on the same template/video with the
 * edits intact after the user steps back to the gallery.
 */
export default function VideoEditorWorkspace({
  template,
  videoUrl,
  videoSources,
  video,
  services,
  userKey,
  docRef,
  onDirty,
  onReadyChange,
  active,
  coverTimestamp,
  onCoverTimestampSave,
  resumeDocument = null,
  // Belongs to the dialog: kept at the editor's current playhead position so a
  // "Capture Thumbnail" action elsewhere can read the frame time on click,
  // the same way `docRef` hands back the current document.
  timeRef = null,
}) {
  // Several raw clips merged back-to-back, instead of the usual single video —
  // the caller has already probed each clip's metadata, so nothing is probed here.
  const multi = Boolean(videoSources && videoSources.length > 1);
  const probe = useVideoMetadata(videoUrl, !multi);
  const ready = multi || probe.status === "ready";
  const templateDocument = template.document;

  // The frame size lets the fit frame a landscape video in a vertical template
  // (or the reverse) over a blurred copy of itself instead of cropping it.
  const fitted = useMemo(() => {
    if (multi) return fitTemplateToVideos(templateDocument, videoSources);
    return ready
      ? fitTemplateToVideo(templateDocument, {
          source: videoUrl,
          duration: probe.duration,
          width: probe.width,
          height: probe.height,
        })
      : null;
  }, [multi, videoSources, ready, templateDocument, videoUrl, probe.duration, probe.width, probe.height]);
  const editorKey = multi
    ? `${template.id}|${template.version ?? ""}|${videoSources.map((v) => v.source).join(",")}`
    : `${template.id}|${template.version ?? ""}|${videoUrl}|${probe.duration}`;

  // Reopening the same template on the same video resumes the edits: from this
  // session's in-memory document, else from the document the caller saved
  // earlier (`resumeDocument`, e.g. a wizard's stored render payload).
  const initialDocument = useMemo(() => {
    if (!fitted) return null;
    const saved = docRef.current;
    if (saved.key === editorKey && saved.doc) return saved.doc;
    return resumeDocument || fitted;
  }, [fitted, editorKey, docRef, resumeDocument]);

  useEffect(() => {
    if (initialDocument && docRef.current.key !== editorKey) {
      docRef.current = { key: editorKey, doc: initialDocument };
    }
  }, [initialDocument, editorKey, docRef]);

  useEffect(() => {
    onReadyChange(Boolean(initialDocument));
  }, [initialDocument, onReadyChange]);
  useEffect(() => () => onReadyChange(false), [onReadyChange]);

  // The editor reports its document on mount too; only a change from that
  // first report is an edit.
  const baselineRef = useRef({ key: null, doc: null });
  const handleDocumentChange = useCallback(
    (doc) => {
      docRef.current = { key: editorKey, doc };
      if (baselineRef.current.key !== editorKey) {
        baselineRef.current = { key: editorKey, doc };
      } else if (doc !== baselineRef.current.doc) {
        onDirty();
      }
    },
    [editorKey, docRef, onDirty],
  );

  // Playhead: kept in a ref every frame, surfaced to state a few times a second.
  const internalTimeRef = useRef(0);
  const timerRef = useRef(null);
  const [coverTime, setCoverTime] = useState(0);
  const handleTimeChange = useCallback((seconds) => {
    internalTimeRef.current = seconds;
    if (timeRef) timeRef.current = seconds;
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setCoverTime(Math.round(internalTimeRef.current));
    }, COVER_READOUT_MS);
  }, [timeRef]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const videoTitle = video?.title || video?.video_title;
  const videoDescription = video?.description;
  const videoInfo = useMemo(
    () => ({ title: videoTitle, description: videoDescription }),
    [videoTitle, videoDescription],
  );

  const hasCover = Boolean(onCoverTimestampSave);
  const tracksTime = hasCover || Boolean(timeRef);

  if (!multi && probe.status !== "ready") {
    return (
      <Box flex={1} display="flex" alignItems="center" justifyContent="center" p={3}>
        {probe.status === "loading" ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Reading the video's length…
            </Typography>
          </Box>
        ) : (
          <Alert severity="error" sx={{ borderRadius: 2, maxWidth: 640 }}>
            {videoUrl
              ? "Could not load this video's metadata."
              : "This video has no source URL to edit."}
            {videoUrl && (
              <Typography variant="caption" component="div" sx={{ mt: 0.5, fontFamily: "monospace", wordBreak: "break-all" }}>
                {videoUrl}
              </Typography>
            )}
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column" gap={1}>
      <Box sx={{ position: "relative", flex: 1, minHeight: 560, borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0" }}>
        <EditorPane
          editorKey={editorKey}
          initialDocument={initialDocument}
          captionTemplate={fitted}
          services={services}
          userKey={userKey}
          video={videoInfo}
          title={template.name}
          active={active}
          onDocumentChange={handleDocumentChange}
          onTimeChange={tracksTime ? handleTimeChange : undefined}
        />
      </Box>

      {/* TikTok cover frame — pause on the frame, then set its time as the cover */}
      {hasCover && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{
            flexShrink: 0,
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: alpha("#010101", 0.04),
            border: "1px solid",
            borderColor: alpha("#010101", 0.15),
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 16, color: "#010101" }} />
          <Typography variant="caption" fontWeight={700} color="#010101">
            TikTok Cover Frame
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => {
              const t = Math.round(internalTimeRef.current);
              onCoverTimestampSave(t);
              toast.success(`Cover frame set at ${formatSeconds(t)}`);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 11,
              py: 0.25,
              color: "#010101",
              borderColor: alpha("#010101", 0.4),
              "&:hover": { borderColor: "#010101", bgcolor: alpha("#010101", 0.06) },
            }}
          >
            Set as Cover ({formatSeconds(coverTime)})
          </Button>
          {coverTimestamp !== null && coverTimestamp !== undefined && coverTimestamp !== "" && (
            <Chip
              size="small"
              label={`Cover @ ${formatSeconds(coverTimestamp)}`}
              onDelete={() => onCoverTimestampSave(null)}
              sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Pause the editor on the frame you want, then set it.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
